/* ASK A VET™ — Pet CPR Timer v2 — Shopify custom element */
(() => {
  'use strict';
  const ELEMENT_NAME = 's-pet-cpr-timer';
  if (customElements.get(ELEMENT_NAME)) return;

  class PetCprTimer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cprInitialised === 'true') return;
      this.dataset.cprInitialised = 'true';
      const root = this;
      const byId = (id) => root.querySelector(`#${CSS.escape(id)}`);
      const CYCLE_MS = 120000;
          const STORAGE_KEY = "askavet-cpr-settings-v2";
          const MODE = {
            nonintubated: {
              short: "30:2 mode",
              result: "Not intubated / 30:2",
              running: "30 compressions, then 2 breaths. Keep the breathing pause brief."
            },
            intubated: {
              short: "Intubated team",
              result: "Intubated / team",
              running: "Continuous compressions. Give 1 breath on each low tone."
            },
            compression: {
              short: "Compression-only",
              result: "Compression-only",
              running: "Continuous compressions. Full recoil after every push."
            }
          };

          const PHASE = {
            IDLE: "idle",
            COMPRESSIONS: "compressions",
            CHECK: "check",
            PAUSED: "paused",
            FINISHED: "finished"
          };

          const els = {
            root: root,
            stage: byId("stage"),
            beatDisc: byId("beatDisc"),
            bigNum: byId("bigNum"),
            bigSub: byId("bigSub"),
            phaseText: byId("phaseText"),
            phaseAnnouncer: byId("phaseAnnouncer"),
            modeBadge: byId("modeBadge"),
            stageMsg: byId("stageMsg"),
            statCycle: byId("statCycle"),
            statTotal: byId("statTotal"),
            statBpm: byId("statBpm"),
            primaryBtn: byId("primaryBtn"),
            endBtn: byId("endBtn"),
            resetBtn: byId("resetBtn"),
            fullscreenBtn: byId("fullscreenBtn"),
            bpm: byId("bpm"),
            bpmVal: byId("bpmVal"),
            checkDuration: byId("checkDuration"),
            checkVal: byId("checkVal"),
            wakeToggle: byId("wakeToggle"),
            testSoundBtn: byId("testSoundBtn"),
            supportStatus: byId("supportStatus"),
            guideHeading: byId("guideHeading"),
            guideList: byId("guideList"),
            monitorNote: byId("monitorNote"),
            quickEventGrid: byId("quickEventGrid"),
            noteForm: byId("noteForm"),
            noteInput: byId("noteInput"),
            noteBtn: byId("noteBtn"),
            epiStatus: byId("epiStatus"),
            emptyLog: byId("emptyLog"),
            eventList: byId("eventList"),
            resultPanel: byId("resultPanel"),
            resultOutcome: byId("resultOutcome"),
            resultDuration: byId("resultDuration"),
            resultCycles: byId("resultCycles"),
            resultMode: byId("resultMode"),
            roscChecklist: byId("roscChecklist"),
            copyBtn: byId("copyBtn"),
            emailBtn: byId("emailBtn"),
            downloadBtn: byId("downloadBtn"),
            printBtn: byId("printBtn"),
            endDialog: byId("endDialog"),
            continueBtn: byId("continueBtn"),
            toast: byId("toast"),
            cycleMinus: byId("cycleMinus"),
            cyclePlus: byId("cyclePlus"),
            dosePanel: byId("dosePanel"),
            weight: byId("weight"),
            wMinus: byId("wMinus"),
            wPlus: byId("wPlus"),
            unitToggle: byId("unitToggle"),
            doseWrap: byId("doseWrap"),
            epiDoseLine: byId("epiDoseLine"),
            speciesSeg: byId("speciesSeg"),
            valsWrap: byId("valsWrap"),
            metroToggleBtn: byId("metroToggleBtn"),
            breathToggleBtn: byId("breathToggleBtn"),
            voiceToggleBtn: byId("voiceToggleBtn"),
            breathMeter: byId("breathMeter"),
            breathMeterLabel: byId("breathMeterLabel"),
            breathFill: byId("breathFill")
          };

          const defaults = {
            bpm: 110,
            checkSec: 8,
            mode: "nonintubated",
            metro: true,
            breathSound: true,
            voice: true,
            wake: true
          };

          const cfg = loadSettings();
          let phase = PHASE.IDLE;
          let previousPhase = PHASE.COMPRESSIONS;
          let running = false;
          let cycleNumber = 0;
          let totalElapsedMs = 0;
          let lastFrameAt = 0;
          let phaseEndsAt = 0;
          let pausedRemainingMs = 0;
          let rafId = 0;
          let eventStartedAt = null;
          let finalOutcome = "";
          let eventLog = [];
          let cueFlags = {};
          let toastTimer = 0;
          let hiddenAt = 0;
          let lastEpinephrineAtMs = null;
          let epinephrineThreeMinuteCue = false;
          let epinephrineFiveMinuteCue = false;

          let audioCtx = null;
          let breathNoiseBuffer = null;
          let schedulerId = 0;
          let fallbackId = 0;
          let nextClickTime = 0;
          let nextBreathTime = 0;
          let patternCompression = 0;
          let continuousBeat = 0;
          let fallbackBreathElapsedMs = 0;
          const visualTimers = new Set();
          const activeOscillators = new Set();
          const LOOKAHEAD_MS = 25;
          const SCHEDULE_AHEAD_SEC = 0.13;

          let wakeLock = null;
          let wakeRequestToken = 0;
          let hotkeysArmed = false;
          let twentyMinuteCue = false;

          // ---------- Patient weight, emergency doses & target values ----------
          let wUnit = "kg";
          let species = "dog";
          const LB_PER_KG = 2.2046226;
          const DOSE_GROUPS = [
            { title: "Give during CPR", rows: [
              { n: "Adrenaline (epinephrine)", short: "Adrenaline", s: "Every other cycle \u00b7 3\u20135 min \u00b7 0.01 mg/kg", perKg: 0.01, u: "mg", conc: 1 },
              { n: "Atropine", short: "Atropine", s: "Once \u00b7 high vagal tone / bradycardia", perKg: 0.04, u: "mg", conc: 0.54 } ] },
            { title: "Shockable rhythm (VF / pulseless VT)", rows: [
              { n: "Lidocaine", short: "Lidocaine", s: "Dogs", perKg: 2, u: "mg", conc: 20 },
              { n: "Amiodarone", short: "Amiodarone", s: "Alternative to lidocaine", perKg: 5, u: "mg", conc: 50 } ] },
            { title: "Reversal agents", rows: [
              { n: "Naloxone", short: "Naloxone", s: "Opioids", perKg: 0.04, u: "mg", conc: 0.4 },
              { n: "Flumazenil", short: "Flumazenil", s: "Benzodiazepines", perKg: 0.01, u: "mg", conc: 0.1 },
              { n: "Atipamezole", short: "Atipamezole", s: "Alpha-2 \u00b7 dexmedetomidine / medetomidine", perKg: 0.1, u: "mg", conc: 5 } ] },
            { title: "Other", rows: [
              { n: "Vasopressin", short: "Vasopressin", s: "Alternative to adrenaline", perKg: 0.8, u: "U", conc: 20 },
              { n: "Sodium bicarbonate", short: "Sodium bicarb", s: "Only if arrest > 10\u201315 min", perKg: 1, u: "mEq", conc: 1 } ] }
          ];
          const CONC_NOTE = "Volumes assume adrenaline 1 mg/mL, atropine 0.54 mg/mL, lidocaine 20 mg/mL, amiodarone 50 mg/mL, naloxone 0.4 mg/mL, flumazenil 0.1 mg/mL, atipamezole 5 mg/mL, vasopressin 20 U/mL, bicarb 1 mEq/mL \u2014 check your own labels, concentrations vary. High-dose adrenaline (0.1 mg/kg) is no longer recommended.";
          const SPECIES_VALS = {
            dog: { label: "Dog", glucose: "4.0\u20136.3 mmol/L", glucoseMg: "72\u2013113 mg/dL", pcv: "40\u201356%", ts: "5.6\u20137.1 g/dL", na: "144\u2013151", k: "3.9\u20135.3", cl: "105\u2013117", ica: "1.25\u20131.45" },
            cat: { label: "Cat", glucose: "3.2\u20136.7 mmol/L", glucoseMg: "58\u2013121 mg/dL", pcv: "28\u201344%", ts: "6.5\u20138.4 g/dL", na: "149\u2013156", k: "3.3\u20135.2", cl: "112\u2013122", ica: "1.10\u20131.40" }
          };

          function currentKg() {
            const value = parseFloat(els.weight.value);
            if (!isFinite(value) || value <= 0) return 0;
            return wUnit === "kg" ? value : value / LB_PER_KG;
          }

          function dsp(x) {
            if (!isFinite(x)) return "\u2014";
            const text = x >= 10 ? x.toFixed(1) : x >= 1 ? x.toFixed(2) : x.toFixed(3);
            return text.indexOf(".") >= 0 ? text.replace(/\.?0+$/, "") : text;
          }

          function renderDoses() {
            const kg = currentKg();
            updateEpiDose(kg);
            if (kg <= 0) {
              els.doseWrap.hidden = true;
              els.doseWrap.innerHTML = "";
              els.dosePanel.setAttribute("data-empty", "");
              return;
            }
            els.dosePanel.removeAttribute("data-empty");
            let html = "";
            DOSE_GROUPS.forEach(function (group) {
              html += '<div class="dose-group"><h4>' + group.title + "</h4>";
              group.rows.forEach(function (row) {
                const amount = row.perKg * kg;
                const ml = amount / row.conc;
                html += '<div class="dose-row"><div class="dose-name">' + row.n + "<small>" + row.s + "</small></div>" +
                  '<div class="dose-val">' + dsp(amount) + " " + row.u + ' <span class="ml">' + dsp(ml) + " mL</span></div>" +
                  '<button class="dose-log" type="button" data-drug="' + row.short + '" data-dose="' + dsp(amount) + " " + row.u + '">Log</button></div>';
              });
              html += "</div>";
            });
            html += '<div class="dose-group"><h4>Defibrillation (external)</h4>' +
              '<div class="dose-row defib-row"><div class="dose-name">Biphasic<small>1st shock \u2192 subsequent</small></div><div class="dose-val">' + dsp(2 * kg) + " J \u2192 " + dsp(4 * kg) + " J</div></div>" +
              '<div class="dose-row defib-row"><div class="dose-name">Monophasic</div><div class="dose-val">' + dsp(4 * kg) + "\u2013" + dsp(6 * kg) + " J</div></div></div>";
            html += '<p class="dose-note"><b>Verify every dose and concentration before giving.</b> Weight-based estimates from the 2024 RECOVER dosing chart for a ' + dsp(kg) + " kg patient. " + CONC_NOTE + "</p>";
            els.doseWrap.innerHTML = html;
            els.doseWrap.hidden = false;
          }

          function updateEpiDose(kg) {
            if (kg > 0) {
              els.epiDoseLine.hidden = false;
              els.epiDoseLine.textContent = "Low dose 0.01 mg/kg = " + dsp(0.01 * kg) + " mg (" + dsp(0.01 * kg) + " mL of 1 mg/mL)";
            } else {
              els.epiDoseLine.hidden = true;
              els.epiDoseLine.textContent = "";
            }
          }

          function stepWeight(direction) {
            const step = wUnit === "kg" ? 0.5 : 1;
            let value = parseFloat(els.weight.value);
            if (!isFinite(value)) value = 0;
            value = Math.max(0, value + direction * step);
            els.weight.value = value ? String(Math.round(value * 10) / 10) : "";
            renderDoses();
          }

          function renderTargets() {
            const sv = SPECIES_VALS[species];
            const rows = [
              ["Oxygen saturation (SpO\u2082)", "", "\u2265 95%", "avoid hypoxaemia"],
              ["ETCO\u2082", "sudden rise may signal ROSC", "&gt; 18 mmHg", "normal 35\u201345"],
              ["Blood pressure", "", "SAP &gt; 90 \u00b7 MAP &gt; 60 mmHg", ""],
              ["Blood glucose", "", sv.glucose, sv.glucoseMg],
              ["PCV / TS", "", "PCV " + sv.pcv, "TS " + sv.ts],
              ["Electrolytes", "", "Na " + sv.na + " \u00b7 K " + sv.k, "Cl " + sv.cl + " \u00b7 iCa " + sv.ica + " mmol/L"],
              ["Ketones (blood BHB)", "", "&lt; 0.6 mmol/L", ""]
            ];
            els.valsWrap.innerHTML = rows.map(function (row) {
              const nameSub = row[1] ? "<small>" + row[1] + "</small>" : "";
              const valueSub = row[3] ? "<small>" + row[3] + "</small>" : "";
              return '<div class="val-row"><div class="vn">' + row[0] + nameSub + '</div><div class="vv">' + row[2] + valueSub + "</div></div>";
            }).join("");
          }

          function setSoundFlag(key) {
            cfg[key] = !cfg[key];
            saveSettings();
            syncSoundButtons();
          }

          function syncSoundButtons() {
            [["metro", els.metroToggleBtn], ["breathSound", els.breathToggleBtn], ["voice", els.voiceToggleBtn]].forEach(function (pair) {
              const on = cfg[pair[0]];
              pair[1].classList.toggle("off", !on);
              pair[1].setAttribute("aria-pressed", on ? "true" : "false");
            });
          }

          function adjustCycle(delta) {
            const minimum = isEventActive() ? 1 : 0;
            const next = Math.max(minimum, (cycleNumber || 0) + delta);
            if (next === cycleNumber) return;
            cycleNumber = next;
            els.statCycle.textContent = String(cycleNumber);
            if (isEventActive()) recordEvent("Cycle count adjusted to " + cycleNumber);
          }

          function initialiseDoses() {
            els.cyclePlus.addEventListener("click", function () { adjustCycle(1); });
            els.cycleMinus.addEventListener("click", function () { adjustCycle(-1); });
            els.weight.addEventListener("input", renderDoses);
            els.wPlus.addEventListener("click", function () { stepWeight(1); });
            els.wMinus.addEventListener("click", function () { stepWeight(-1); });
            els.unitToggle.addEventListener("click", function () {
              const value = parseFloat(els.weight.value);
              if (wUnit === "kg") {
                wUnit = "lb";
                els.unitToggle.textContent = "lb";
                els.weight.step = "1";
                if (isFinite(value) && value > 0) els.weight.value = (value * LB_PER_KG).toFixed(1).replace(/\.0$/, "");
              } else {
                wUnit = "kg";
                els.unitToggle.textContent = "kg";
                els.weight.step = "0.5";
                if (isFinite(value) && value > 0) els.weight.value = (value / LB_PER_KG).toFixed(1).replace(/\.0$/, "");
              }
              renderDoses();
            });
            els.doseWrap.addEventListener("click", function (event) {
              const log = event.target.closest(".dose-log");
              if (!log) return;
              if (!isEventActive()) {
                showToast("Start the timer to log doses in the event timeline.");
                return;
              }
              if (log.dataset.drug === "Adrenaline") {
                lastEpinephrineAtMs = totalElapsedMs;
                epinephrineThreeMinuteCue = false;
                epinephrineFiveMinuteCue = false;
                updateEpinephrineReminder();
              }
              recordEvent(log.dataset.drug + " " + log.dataset.dose + " given", true);
              log.textContent = "Logged \u2713";
              log.classList.add("done");
              window.setTimeout(function () { log.textContent = "Log"; log.classList.remove("done"); }, 1200);
            });
            els.speciesSeg.querySelectorAll("button").forEach(function (button) {
              button.addEventListener("click", function () {
                species = button.dataset.species;
                els.speciesSeg.querySelectorAll("button").forEach(function (other) { other.classList.toggle("sel", other === button); });
                renderTargets();
              });
            });
            renderTargets();
          }

          initialise();

          function initialise() {
            els.bpm.value = String(cfg.bpm);
            els.checkDuration.value = String(cfg.checkSec);
            els.wakeToggle.checked = cfg.wake;
            syncSoundButtons();
            els.bpmVal.textContent = String(cfg.bpm);
            els.checkVal.textContent = String(cfg.checkSec);
            els.statBpm.textContent = String(cfg.bpm);
            const modeRadio = els.root.querySelector('input[name="cprMode"][value="' + cfg.mode + '"]');
            if (modeRadio) modeRadio.checked = true;
            updateModePresentation(false);
            updateSupportStatus();
            bindEvents();
            initialiseDoses();
          }

          function loadSettings() {
            try {
              const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
              const checkSec = Number(saved.checkSec);
              const legacySound = typeof saved.sound === "boolean" ? saved.sound : true;
              return {
                bpm: defaults.bpm,
                checkSec: checkSec >= 5 && checkSec <= 9 ? Math.round(checkSec) : defaults.checkSec,
                mode: MODE[saved.mode] ? saved.mode : defaults.mode,
                metro: typeof saved.metro === "boolean" ? saved.metro : legacySound,
                breathSound: typeof saved.breathSound === "boolean" ? saved.breathSound : legacySound,
                voice: typeof saved.voice === "boolean" ? saved.voice : defaults.voice,
                wake: typeof saved.wake === "boolean" ? saved.wake : defaults.wake
              };
            } catch (error) {
              return Object.assign({}, defaults);
            }
          }

          function saveSettings() {
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg)); } catch (error) {}
          }

          function bindEvents() {
            els.primaryBtn.addEventListener("click", function () {
              if (phase === PHASE.IDLE) startEvent();
              else if (phase === PHASE.CHECK) endRhythmCheckEarly();
              else if (phase === PHASE.PAUSED) resumeEvent();
              else if (running) pauseEvent();
            });

            els.beatDisc.addEventListener("click", function () {
              if (els.primaryBtn.disabled) return;
              els.primaryBtn.click();
            });

            els.endBtn.addEventListener("click", openEndDialog);
            els.continueBtn.addEventListener("click", closeEndDialog);
            els.endDialog.querySelectorAll("[data-finish]").forEach(function (button) {
              button.addEventListener("click", function () {
                closeEndDialog();
                finishEvent(button.dataset.finish);
              });
            });
            els.resetBtn.addEventListener("click", resetEvent);
            els.fullscreenBtn.addEventListener("click", toggleFullscreen);
            els.testSoundBtn.addEventListener("click", testSound);

            els.root.querySelectorAll('input[name="cprMode"]').forEach(function (radio) {
              radio.addEventListener("change", function () {
                cfg.mode = radio.value;
                saveSettings();
                updateModePresentation(true);
                if (phase === PHASE.COMPRESSIONS) {
                  patternCompression = 0;
                  restartScheduler();
                  recordEvent("Mode changed: " + MODE[cfg.mode].result);
                }
              });
            });

            els.bpm.addEventListener("input", function () {
              cfg.bpm = Number(els.bpm.value);
              els.bpmVal.textContent = String(cfg.bpm);
              els.statBpm.textContent = String(cfg.bpm);
              saveSettings();
              if (phase === PHASE.COMPRESSIONS) restartScheduler();
            });

            els.checkDuration.addEventListener("input", function () {
              cfg.checkSec = Number(els.checkDuration.value);
              els.checkVal.textContent = String(cfg.checkSec);
              saveSettings();
            });

            els.metroToggleBtn.addEventListener("click", function () { setSoundFlag("metro"); });
            els.breathToggleBtn.addEventListener("click", function () { setSoundFlag("breathSound"); });
            els.voiceToggleBtn.addEventListener("click", function () {
              setSoundFlag("voice");
              if (!cfg.voice) cancelSpeech();
            });

            els.wakeToggle.addEventListener("change", function () {
              cfg.wake = els.wakeToggle.checked;
              saveSettings();
              if (cfg.wake && running) requestWakeLock();
              else releaseWakeLock();
              updateSupportStatus();
            });

            els.quickEventGrid.addEventListener("click", function (event) {
              const button = event.target.closest("button");
              if (!button || button.disabled) return;
              if (button.dataset.action === "rosc") {
                openEndDialog();
                return;
              }
              if (button.dataset.event === "Epinephrine given") {
                recordEpinephrine();
                return;
              }
              if (button.dataset.event) recordEvent(button.dataset.event, true);
            });

            els.noteForm.addEventListener("submit", function (event) {
              event.preventDefault();
              const note = els.noteInput.value.trim();
              if (!note || !isEventActive()) return;
              recordEvent("Note: " + note, true);
              els.noteInput.value = "";
            });

            els.copyBtn.addEventListener("click", copyResults);
            els.emailBtn.addEventListener("click", emailResults);
            els.downloadBtn.addEventListener("click", downloadResults);
            els.printBtn.addEventListener("click", function () { window.print(); });

            document.addEventListener("visibilitychange", function () {
              if (document.visibilityState === "hidden" && running) {
                hiddenAt = performance.now();
                stopScheduler();
                return;
              }
              if (document.visibilityState === "visible" && running) {
                const now = performance.now();
                const interruptionMs = hiddenAt ? Math.max(0, now - hiddenAt) : 0;
                hiddenAt = 0;
                if (interruptionMs > 1500) {
                  resynchroniseAfterInterruption(now, interruptionMs);
                } else if (phase === PHASE.COMPRESSIONS) {
                  restartScheduler();
                }
                if (cfg.wake) requestWakeLock();
              }
            });
            document.addEventListener("fullscreenchange", updateFullscreenButton);

            window.addEventListener("beforeunload", function (event) {
              if (!isEventActive()) return;
              event.preventDefault();
              event.returnValue = "";
            });

            els.root.addEventListener("pointerdown", function (event) {
              hotkeysArmed = true;
              if (!event.target.closest("button, input, textarea, select, a, summary, label, dialog")) {
                els.root.focus({ preventScroll: true });
              }
            });
            document.addEventListener("pointerdown", function (event) {
              if (!els.root.contains(event.target)) hotkeysArmed = false;
            }, true);
            document.addEventListener("focusin", function (event) {
              hotkeysArmed = els.root.contains(event.target);
            });
            document.addEventListener("keydown", function (event) {
              const target = event.target;
              const editing = target instanceof Element && Boolean(target.closest("button, input, textarea, select, a, summary, [contenteditable='true']"));
              if (hotkeysArmed && event.code === "Space" && !editing && !els.endDialog.open) {
                event.preventDefault();
                els.primaryBtn.click();
              }
            });
          }

          function isEventActive() {
            return phase === PHASE.COMPRESSIONS || phase === PHASE.CHECK || phase === PHASE.PAUSED;
          }

          function startEvent() {
            ensureAudio();
            eventLog = [];
            eventStartedAt = new Date();
            finalOutcome = "";
            totalElapsedMs = 0;
            cycleNumber = 1;
            lastEpinephrineAtMs = null;
            epinephrineThreeMinuteCue = false;
            epinephrineFiveMinuteCue = false;
            twentyMinuteCue = false;
            updateEpinephrineReminder();
            els.resultPanel.removeAttribute("data-outcome");
            els.resultPanel.hidden = true;
            els.roscChecklist.hidden = true;
            setEventControls(true);
            els.root.classList.add("event-active");
            recordEvent("CPR timer started — " + MODE[cfg.mode].result);
            beginCompressionCycle(false);
            requestWakeLock();
            scheduleLoop();
          }

          function beginCompressionCycle(isNewCycle) {
            if (isNewCycle) cycleNumber += 1;
            phase = PHASE.COMPRESSIONS;
            running = true;
            previousPhase = PHASE.COMPRESSIONS;
            lastFrameAt = performance.now();
            phaseEndsAt = lastFrameAt + CYCLE_MS;
            resetCycleCues();
            updatePhaseView();
            restartScheduler(true);
            say("Cycle " + cycleNumber + ". Begin compressions.", true);
          }

          function enterRhythmCheck() {
            if (cfg.mode !== "intubated") {
              recordEvent("Cycle " + cycleNumber + " completed");
              cycleNumber += 1;
              phaseEndsAt = performance.now() + CYCLE_MS;
              cueFlags = {};
              els.statCycle.textContent = String(cycleNumber);
              els.phaseAnnouncer.textContent = "Two minutes complete. Continue CPR and switch compressor with minimal interruption if help is available.";
              say("Two minutes complete. Switch compressor with less than one second interruption. Continue C P R.", true);
              return;
            }
            recordEvent("Cycle " + cycleNumber + " completed; rhythm check started");
            phase = PHASE.CHECK;
            previousPhase = PHASE.CHECK;
            phaseEndsAt = performance.now() + cfg.checkSec * 1000;
            resetCycleCues();
            stopScheduler();
            updatePhaseView();
            say("Pause and check. E C G rhythm and femoral pulse. Switch compressor. Resume in under ten seconds.", true);
          }

          function endRhythmCheckEarly() {
            if (phase !== PHASE.CHECK) return;
            recordEvent("No pulse; compressions resumed early");
            beginCompressionCycle(true);
          }

          function pauseEvent() {
            if (!running) return;
            syncElapsed(performance.now());
            pausedRemainingMs = Math.max(0, phaseEndsAt - performance.now());
            previousPhase = phase;
            phase = PHASE.PAUSED;
            running = false;
            cancelAnimationFrame(rafId);
            stopScheduler();
            releaseWakeLock();
            cancelSpeech();
            recordEvent("Timer manually paused");
            updatePhaseView();
          }

          function resumeEvent() {
            if (phase !== PHASE.PAUSED) return;
            ensureAudio();
            phase = previousPhase;
            running = true;
            lastFrameAt = performance.now();
            phaseEndsAt = lastFrameAt + Math.max(250, pausedRemainingMs);
            recordEvent("Timer resumed");
            updatePhaseView();
            if (phase === PHASE.COMPRESSIONS) restartScheduler();
            say(phase === PHASE.CHECK ? "Resume rhythm and pulse check." : "Resume compressions.", true);
            requestWakeLock();
            scheduleLoop();
          }

          function finishEvent(outcome) {
            if (!isEventActive()) return;
            if (running) syncElapsed(performance.now());
            running = false;
            stopScheduler();
            cancelAnimationFrame(rafId);
            releaseWakeLock();
            cancelSpeech();
            finalOutcome = outcome;
            phase = PHASE.FINISHED;
            recordEvent(outcome);
            els.root.classList.remove("event-active");
            setEventControls(false);
            updatePhaseView();
            renderResult();
            say(outcome === "ROSC achieved" ? "Pulse confirmed. Return of spontaneous circulation." : "C P R event ended.", true);
            els.resultPanel.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }

          function resetEvent() {
            if (isEventActive()) {
              const confirmed = window.confirm("Start again and permanently clear the current CPR event log?");
              if (!confirmed) return;
            }
            running = false;
            stopScheduler();
            cancelAnimationFrame(rafId);
            releaseWakeLock();
            cancelSpeech();
            phase = PHASE.IDLE;
            previousPhase = PHASE.COMPRESSIONS;
            cycleNumber = 0;
            totalElapsedMs = 0;
            pausedRemainingMs = 0;
            eventStartedAt = null;
            finalOutcome = "";
            eventLog = [];
            hiddenAt = 0;
            lastEpinephrineAtMs = null;
            epinephrineThreeMinuteCue = false;
            epinephrineFiveMinuteCue = false;
            twentyMinuteCue = false;
            updateEpinephrineReminder();
            els.root.classList.remove("event-active");
            els.resultPanel.removeAttribute("data-outcome");
            els.resultPanel.hidden = true;
            els.roscChecklist.hidden = true;
            setEventControls(false);
            renderLog();
            updatePhaseView();
          }

          function resynchroniseAfterInterruption(now, interruptionMs) {
            syncElapsed(now);
            cancelAnimationFrame(rafId);
            cycleNumber += 1;
            phase = PHASE.COMPRESSIONS;
            previousPhase = PHASE.COMPRESSIONS;
            phaseEndsAt = now + CYCLE_MS;
            lastFrameAt = now;
            resetCycleCues();
            updatePhaseView();
            restartScheduler(true);
            recordEvent("Timer interrupted for " + Math.ceil(interruptionMs / 1000) + " sec; compression cycle resynchronised");
            showToast("Timer interrupted — continue compressions and switch compressor now if possible.");
            say("Timer interrupted. Continue compressions and switch compressor now if possible.", true);
            scheduleLoop();
          }

          function scheduleLoop() {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(loop);
          }

          function loop(now) {
            if (!running) return;
            syncElapsed(now);
            const remainingMs = Math.max(0, phaseEndsAt - now);

            if (phase === PHASE.COMPRESSIONS) {
              updateCompressionDisplay(remainingMs);
              runCompressionVoiceCues(remainingMs);
              if (remainingMs <= 0) enterRhythmCheck();
            } else if (phase === PHASE.CHECK) {
              const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
              setBigNumber(String(seconds), false);
              els.bigSub.textContent = "ECG rhythm + femoral pulse";
              els.stageMsg.textContent = "Switch compressor. No pulse? Resume immediately.";
              runCheckVoiceCues(seconds);
              if (remainingMs <= 0) {
                recordEvent("Check ended; compressions auto-resumed");
                beginCompressionCycle(true);
              }
            }

            els.statTotal.textContent = formatElapsed(totalElapsedMs);
            if (!twentyMinuteCue && totalElapsedMs >= 1200000) {
              twentyMinuteCue = true;
              recordEvent("20 minutes elapsed \u2014 team prompted to reassess");
              showToast("20 minutes of CPR \u2014 reassess whether to continue.");
              say("Twenty minutes elapsed. Reassess whether to continue.", false);
            }
            updateEpinephrineReminder();
            if (running) rafId = requestAnimationFrame(loop);
          }

          function syncElapsed(now) {
            const delta = Math.max(0, now - lastFrameAt);
            totalElapsedMs += delta;
            lastFrameAt = now;
            els.statTotal.textContent = formatElapsed(totalElapsedMs);
          }

          function updateCompressionDisplay(remainingMs) {
            const remaining = formatCountdown(remainingMs);
            if (cfg.mode === "nonintubated") {
              if (!els.beatDisc.dataset.cueActive) {
                els.bigSub.textContent = "compression · " + remaining + " left";
                els.stageMsg.textContent = MODE[cfg.mode].running;
              }
              return;
            }
            if (els.beatDisc.dataset.cueActive) return;
            setBigNumber(remaining, false);
            els.bigSub.textContent = "until cycle change";
            els.stageMsg.textContent = MODE[cfg.mode].running;
          }

          function runCompressionVoiceCues(remainingMs) {
            if (remainingMs <= 30000 && !cueFlags.thirty) {
              cueFlags.thirty = true;
              const checkText = cfg.mode === "intubated" ? "Prepare to switch compressor and check the rhythm." : "Prepare to switch compressor if help is available.";
              say("Thirty seconds. " + checkText, false);
            }
            if (remainingMs <= 10000 && !cueFlags.ten) {
              cueFlags.ten = true;
              say(cfg.mode === "intubated" ? "Ten seconds. Prepare for rhythm and pulse check." : "Ten seconds. Prepare to switch compressor.", false);
            }
            [3, 2, 1].forEach(function (second) {
              const key = "s" + second;
              if (remainingMs <= second * 1000 && !cueFlags[key]) {
                cueFlags[key] = true;
                say(String(second), true);
              }
            });
          }

          function runCheckVoiceCues(seconds) {
            if (seconds <= 3 && seconds > 0) {
              const key = "check" + seconds;
              if (!cueFlags[key]) {
                cueFlags[key] = true;
                say(String(seconds), true);
              }
            }
          }

          function resetCycleCues() {
            cueFlags = {};
            delete els.beatDisc.dataset.cueActive;
          }

          function updatePhaseView() {
            els.stage.className = "timer-stage " + phase;
            els.statCycle.textContent = String(cycleNumber || 0);
            els.statTotal.textContent = formatElapsed(totalElapsedMs);
            els.statBpm.textContent = String(cfg.bpm);
            els.modeBadge.textContent = MODE[cfg.mode].short;

            if (phase === PHASE.IDLE) {
              els.phaseText.textContent = "Ready";
              setBigNumber("CPR", true);
              els.bigSub.textContent = "Tap to start";
              els.stageMsg.textContent = "100–120/min. Compress, fully release, repeat.";
              els.primaryBtn.textContent = "Start CPR";
              els.primaryBtn.classList.remove("running");
              els.primaryBtn.disabled = false;
              els.endBtn.disabled = true;
              els.resetBtn.disabled = true;
            } else if (phase === PHASE.COMPRESSIONS) {
              els.phaseText.textContent = "Compressions";
              if (cfg.mode === "nonintubated") {
                setBigNumber("1", false);
                els.bigSub.textContent = "compression · 2:00 left";
              } else {
                setBigNumber(formatCountdown(Math.max(0, phaseEndsAt - performance.now())), false);
                els.bigSub.textContent = "until cycle change";
              }
              els.stageMsg.textContent = MODE[cfg.mode].running;
              els.primaryBtn.textContent = "Pause timer";
              els.primaryBtn.classList.add("running");
              els.primaryBtn.disabled = false;
              els.endBtn.disabled = false;
              els.resetBtn.disabled = true;
            } else if (phase === PHASE.CHECK) {
              els.phaseText.textContent = "Rhythm + pulse check";
              els.stageMsg.textContent = "ECG rhythm, femoral pulse, switch compressor — under 10 seconds.";
              els.primaryBtn.textContent = "No pulse — resume now";
              els.primaryBtn.classList.add("running");
              els.primaryBtn.disabled = false;
              els.endBtn.disabled = false;
              els.resetBtn.disabled = true;
            } else if (phase === PHASE.PAUSED) {
              els.phaseText.textContent = "Timer paused";
              setBigNumber(formatElapsed(totalElapsedMs), false);
              els.bigSub.textContent = "event time";
              els.stageMsg.textContent = "CPR timing is paused. Resume only if compressions are continuing.";
              els.primaryBtn.textContent = "Resume timer";
              els.primaryBtn.classList.remove("running");
              els.primaryBtn.disabled = false;
              els.endBtn.disabled = false;
              els.resetBtn.disabled = false;
            } else if (phase === PHASE.FINISHED) {
              els.phaseText.textContent = "Event ended";
              setBigNumber(finalOutcome === "ROSC achieved" ? "ROSC" : "ENDED", true);
              els.bigSub.textContent = formatElapsed(totalElapsedMs) + " total";
              els.stageMsg.textContent = finalOutcome;
              els.primaryBtn.textContent = "Event complete";
              els.primaryBtn.classList.remove("running");
              els.primaryBtn.disabled = true;
              els.endBtn.disabled = true;
              els.resetBtn.disabled = false;
            }
            announcePhase();
            updateGuide();
          }

          function setBigNumber(text, isWord) {
            els.bigNum.textContent = text;
            els.bigNum.classList.toggle("word", Boolean(isWord));
          }

          function announcePhase() {
            const announcements = {
              idle: "CPR timer ready.",
              compressions: "Compression cycle " + cycleNumber + " started.",
              check: "Rhythm and femoral pulse check. Resume within ten seconds.",
              paused: "CPR timer paused.",
              finished: "CPR event ended. " + finalOutcome
            };
            els.phaseAnnouncer.textContent = announcements[phase] || "";
          }

          function updateModePresentation(logChange) {
            els.modeBadge.textContent = MODE[cfg.mode].short;
            if (phase === PHASE.COMPRESSIONS) els.stageMsg.textContent = MODE[cfg.mode].running;
            updateGuide();
            if (logChange && isEventActive()) showToast("Mode updated: " + MODE[cfg.mode].result);
          }

          function updateGuide() {
            if (phase === PHASE.CHECK) {
              els.guideHeading.textContent = "During this brief check";
              els.guideList.innerHTML = "<li>Read the ECG rhythm and palpate the femoral pulse.</li><li>Switch compressor while minimising hands-off time.</li><li>No pulse + VF/pulseless VT: shockable rhythm.</li><li>No pulse + asystole/PEA: non-shockable rhythm.</li>";
              els.monitorNote.innerHTML = "<strong>Do not wait for zero:</strong> if there is no pulse, use <em>No pulse — resume now</em>. A pulse means ROSC and post-arrest care.";
              return;
            }

            if (phase === PHASE.COMPRESSIONS) {
              els.guideHeading.textContent = "Live quality cues";
              if (cfg.mode === "intubated") {
                els.guideList.innerHTML = "<li>Continuous compressions at 100–120/min.</li><li>One breath every 6 seconds; about 1 second inspiration.</li><li>Full recoil and no leaning between compressions.</li><li>Prepare the next compressor before 2 minutes.</li>";
                els.monitorNote.innerHTML = "<strong>Professional monitoring:</strong> continuous ECG and waveform capnography where available; optimise CPR toward ETCO₂ ≥18 mmHg.";
              } else if (cfg.mode === "compression") {
                els.guideList.innerHTML = "<li>Continuous compressions at 100–120/min.</li><li>Allow full chest recoil after every compression.</li><li>Switch compressor at 2 minutes with &lt;1 second interruption if help is available.</li><li>Contact an emergency vet and arrange transport.</li>";
                els.monitorNote.innerHTML = "<strong>Compression-only:</strong> selected because breaths are unsafe or suitable barrier/manual ventilation equipment is unavailable.";
              } else {
                els.guideList.innerHTML = "<li>Follow the numbered count to 30 compressions.</li><li>Give 2 breaths during the brief tone-guided pause.</li><li>Each breath takes about 1 second with visible, not excessive, chest rise.</li><li>Resume compressions immediately after breath 2.</li>";
                els.monitorNote.innerHTML = "<strong>Rescuer safety:</strong> mouth-to-snout is only appropriate when zoonotic disease and toxicant or narcotic exposure risk is minimal.";
              }
              return;
            }

            els.guideHeading.textContent = phase === PHASE.FINISHED ? "Event record ready" : "Before you press start";
            els.guideList.innerHTML = phase === PHASE.FINISHED
              ? "<li>Review the timestamped timeline.</li><li>Copy, email, download or print the record.</li><li>If ROSC occurred, begin post-arrest monitoring and treatment immediately.</li>"
              : "<li>Call for help and contact an emergency veterinary clinic.</li><li>Confirm the patient is unresponsive and not breathing normally.</li><li>Single rescuer: briefly check and clear an obvious airway obstruction.</li><li>Start compressions without delaying to search for a pulse.</li>";
            els.monitorNote.innerHTML = "<strong>Selected:</strong> " + MODE[cfg.mode].result + ". This timer is for adult dogs and cats only.";
          }

          function setEventControls(active) {
            els.quickEventGrid.querySelectorAll("button").forEach(function (button) { button.disabled = !active; });
            els.noteInput.disabled = !active;
            els.noteBtn.disabled = !active;
          }

          function recordEvent(label, notify) {
            eventLog.push({ timeMs: totalElapsedMs, label: label });
            renderLog();
            if (notify) showToast(label + " recorded at " + formatElapsed(totalElapsedMs));
          }

          function recordEpinephrine() {
            lastEpinephrineAtMs = totalElapsedMs;
            epinephrineThreeMinuteCue = false;
            epinephrineFiveMinuteCue = false;
            recordEvent("Epinephrine given", true);
            updateEpinephrineReminder();
          }

          function updateEpinephrineReminder() {
            if (lastEpinephrineAtMs === null) {
              els.epiStatus.textContent = "Not started";
              return;
            }

            const elapsed = Math.max(0, totalElapsedMs - lastEpinephrineAtMs);
            if (elapsed < 180000) {
              els.epiStatus.textContent = "3–5 min interval opens in " + formatElapsed(180000 - elapsed);
              return;
            }

            if (!epinephrineThreeMinuteCue) {
              epinephrineThreeMinuteCue = true;
              recordEvent("Epinephrine 3-minute interval reached");
              say("Three minute epinephrine interval reached. Check the rhythm and protocol.", false);
            }

            if (elapsed < 300000) {
              els.epiStatus.textContent = "3–5 min interval window open";
              return;
            }

            if (!epinephrineFiveMinuteCue) {
              epinephrineFiveMinuteCue = true;
              recordEvent("Epinephrine 5-minute interval reached");
              say("Five minute epinephrine interval reached. Check the rhythm and protocol.", false);
            }
            els.epiStatus.textContent = "5 min elapsed — review rhythm and protocol";
          }

          function renderLog() {
            els.eventList.replaceChildren();
            els.emptyLog.hidden = eventLog.length > 0;
            eventLog.forEach(function (item) {
              const row = document.createElement("li");
              const time = document.createElement("span");
              const label = document.createElement("span");
              time.className = "event-time";
              time.textContent = formatElapsed(item.timeMs);
              label.textContent = item.label;
              row.append(time, label);
              els.eventList.append(row);
            });
            if (eventLog.length) els.eventList.scrollTop = els.eventList.scrollHeight;
          }

          function renderResult() {
            els.resultOutcome.textContent = finalOutcome;
            els.resultDuration.textContent = formatElapsed(totalElapsedMs);
            els.resultCycles.textContent = String(cycleNumber);
            els.resultMode.textContent = MODE[cfg.mode].result;
            els.roscChecklist.hidden = finalOutcome !== "ROSC achieved";
            els.resultPanel.dataset.outcome = finalOutcome === "ROSC achieved" ? "rosc" : "no-rosc";
            els.resultPanel.hidden = false;
          }

          function buildResultText() {
            const start = eventStartedAt ? eventStartedAt.toLocaleString() : "Not recorded";
            const lines = [
              "ASK A VET™ — DOG & CAT CPR EVENT RECORD",
              "",
              "Started: " + start,
              "Outcome: " + (finalOutcome || "Event in progress"),
              "Recorded duration: " + formatElapsed(totalElapsedMs),
              "Compression cycles started: " + cycleNumber,
              "Final CPR mode: " + MODE[cfg.mode].result,
              "Compression rate: " + cfg.bpm + "/min",
              "",
              "EVENT TIMELINE"
            ];
            const kg = currentKg();
            if (kg > 0) lines.splice(8, 0, "Patient weight: " + dsp(kg) + " kg");
            eventLog.forEach(function (item) { lines.push(formatElapsed(item.timeMs) + " — " + item.label); });
            if (kg > 0) {
              lines.push("", "EMERGENCY DOSES (" + dsp(kg) + " kg) — 2024 RECOVER dosing chart");
              DOSE_GROUPS.forEach(function (group) {
                group.rows.forEach(function (row) {
                  const amount = row.perKg * kg;
                  lines.push("  " + row.short + ": " + dsp(amount) + " " + row.u + " (" + dsp(amount / row.conc) + " mL)");
                });
              });
              lines.push("  Defibrillation: biphasic " + dsp(2 * kg) + " J then " + dsp(4 * kg) + " J; monophasic " + dsp(4 * kg) + "\u2013" + dsp(6 * kg) + " J");
              lines.push("  Verify every dose and concentration before giving.");
            }
            lines.push("", "Timing aid only; verify against the clinical record and local protocol.");
            return lines.join("\n");
          }

          async function copyResults() {
            const text = buildResultText();
            try {
              await navigator.clipboard.writeText(text);
              showToast("Results copied.");
            } catch (error) {
              const area = document.createElement("textarea");
              area.value = text;
              area.setAttribute("readonly", "");
              area.style.position = "fixed";
              area.style.opacity = "0";
              els.root.appendChild(area);
              area.select();
              const copied = document.execCommand("copy");
              area.remove();
              showToast(copied ? "Results copied." : "Copy was blocked. Use Download log instead.");
            }
          }

          function emailResults() {
            const subject = encodeURIComponent("Dog & Cat CPR event record");
            const body = encodeURIComponent(buildResultText());
            window.location.href = "mailto:?subject=" + subject + "&body=" + body;
          }

          function downloadResults() {
            const blob = new Blob([buildResultText()], { type: "text/plain;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            const stamp = eventStartedAt ? eventStartedAt.toISOString().slice(0, 19).replace(/[:T]/g, "-") : "event";
            link.href = url;
            link.download = "askavet-cpr-" + stamp + ".txt";
            els.root.appendChild(link);
            link.click();
            link.remove();
            setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
            showToast("Event log downloaded.");
          }

          function formatElapsed(ms) {
            const totalSeconds = Math.max(0, Math.floor(ms / 1000));
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            return hours > 0
              ? hours + ":" + String(minutes).padStart(2, "0") + ":" + String(seconds).padStart(2, "0")
              : minutes + ":" + String(seconds).padStart(2, "0");
          }

          function formatCountdown(ms) {
            const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            return minutes + ":" + String(seconds).padStart(2, "0");
          }

          function ensureAudio() {
            if (audioCtx) {
              if (audioCtx.state === "suspended") audioCtx.resume().catch(function () {});
              return true;
            }
            try {
              const AudioContextClass = window.AudioContext || window.webkitAudioContext;
              if (!AudioContextClass) return false;
              audioCtx = new AudioContextClass();
              if (audioCtx.state === "suspended") audioCtx.resume().catch(function () {});
              return true;
            } catch (error) {
              audioCtx = null;
              updateSupportStatus("Audio is unavailable; the visual timer will continue.");
              return false;
            }
          }

          function restartScheduler(resetPattern) {
            stopScheduler();
            if (phase !== PHASE.COMPRESSIONS) return;
            if (resetPattern) patternCompression = 0;
            continuousBeat = 0;
            if (ensureAudio()) {
              nextClickTime = audioCtx.currentTime + 0.08;
              nextBreathTime = audioCtx.currentTime + 6;
              schedulerId = window.setInterval(audioScheduler, LOOKAHEAD_MS);
              audioScheduler();
            } else {
              fallbackBreathElapsedMs = 0;
              fallbackStep();
            }
          }

          function audioScheduler() {
            if (phase !== PHASE.COMPRESSIONS || !audioCtx) return;
            const interval = 60 / cfg.bpm;
            const horizon = audioCtx.currentTime + SCHEDULE_AHEAD_SEC;
            let scheduled = 0;

            while (nextClickTime < horizon && scheduled < 8) {
              scheduled += 1;
              if (nextClickTime < audioCtx.currentTime - 0.2) nextClickTime = audioCtx.currentTime + 0.03;
              if (cfg.mode === "nonintubated") {
                patternCompression += 1;
                const count = patternCompression;
                scheduleCompressionCue(nextClickTime, count, count === 1 || count === 30);
                if (count === 30) {
                  scheduleBreathCue(nextClickTime + 0.55, 1, true);
                  scheduleBreathCue(nextClickTime + 1.70, 2, true);
                  nextClickTime += 3.05;
                  patternCompression = 0;
                } else {
                  nextClickTime += interval;
                }
              } else {
                continuousBeat += 1;
                scheduleCompressionCue(nextClickTime, 0, continuousBeat % 10 === 1);
                nextClickTime += interval;
              }
            }

            if (cfg.mode === "intubated") {
              if (nextBreathTime < audioCtx.currentTime - 0.2) nextBreathTime = audioCtx.currentTime + 6;
              while (nextBreathTime < horizon) {
                scheduleBreathCue(nextBreathTime, 0, false);
                nextBreathTime += 6;
              }
            }
          }

          function fallbackStep() {
            if (phase !== PHASE.COMPRESSIONS) return;
            const intervalMs = 60000 / cfg.bpm;
            let nextDelay = intervalMs;
            if (cfg.mode === "nonintubated") {
              patternCompression += 1;
              displayCompressionCue(patternCompression);
              if (patternCompression === 30) {
                const one = window.setTimeout(function () { displayBreathCue(1, true); }, 550);
                const two = window.setTimeout(function () { displayBreathCue(2, true); }, 1700);
                visualTimers.add(one);
                visualTimers.add(two);
                patternCompression = 0;
                nextDelay = 3050;
              }
            } else {
              continuousBeat += 1;
              popBeat();
              if (cfg.mode === "intubated") {
                fallbackBreathElapsedMs += intervalMs;
                if (fallbackBreathElapsedMs >= 6000) {
                  fallbackBreathElapsedMs = 0;
                  displayBreathCue(0, false);
                }
              }
            }
            fallbackId = window.setTimeout(fallbackStep, nextDelay);
          }

          function scheduleCompressionCue(time, count, accent) {
            if (cfg.metro) scheduleTone(time, accent ? 1350 : 960, accent ? 0.42 : 0.28, 0.055, "square");
            scheduleVisual(time, function () {
              if (phase !== PHASE.COMPRESSIONS) return;
              popBeat();
              if (cfg.mode === "nonintubated") displayCompressionCue(count);
            });
          }

          function scheduleBreathCue(time, breathNumber, pausesCompressions) {
            if (cfg.breathSound) {
              if (pausesCompressions) scheduleBreathNoise(time, 0.6, 0.45);
              else scheduleBreathNoise(time, 1.0, 1.1);
            }
            scheduleVisual(time, function () {
              if (phase !== PHASE.COMPRESSIONS) return;
              displayBreathCue(breathNumber, pausesCompressions);
            });
          }

          function makeBreathNoiseBuffer() {
            const seconds = 2;
            const length = Math.floor(audioCtx.sampleRate * seconds);
            const buffer = audioCtx.createBuffer(1, length, audioCtx.sampleRate);
            const data = buffer.getChannelData(0);
            let last = 0;
            for (let i = 0; i < length; i++) {
              const white = Math.random() * 2 - 1;
              last = (last + 0.02 * white) / 1.02;
              data[i] = last * 3.5;
            }
            return buffer;
          }

          function scheduleBreathPhase(startTime, duration, fromHz, toHz, peakVolume) {
            const source = audioCtx.createBufferSource();
            source.buffer = breathNoiseBuffer;
            source.loop = true;
            const filter = audioCtx.createBiquadFilter();
            filter.type = "bandpass";
            filter.Q.value = 0.8;
            filter.frequency.setValueAtTime(fromHz, startTime);
            filter.frequency.exponentialRampToValueAtTime(toHz, startTime + duration);
            const gain = audioCtx.createGain();
            gain.gain.setValueAtTime(0.0001, startTime);
            gain.gain.exponentialRampToValueAtTime(peakVolume, startTime + duration * 0.4);
            gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
            source.connect(filter).connect(gain).connect(audioCtx.destination);
            activeOscillators.add(source);
            source.onended = function () { activeOscillators.delete(source); };
            source.start(startTime);
            source.stop(startTime + duration + 0.03);
          }

          function scheduleBreathNoise(time, inhaleDur, exhaleDur) {
            if (!audioCtx) return;
            try {
              if (!breathNoiseBuffer) breathNoiseBuffer = makeBreathNoiseBuffer();
              scheduleBreathPhase(time, inhaleDur, 340, 1500, 0.55);
              scheduleBreathPhase(time + inhaleDur + 0.06, exhaleDur, 1100, 260, 0.4);
            } catch (error) {}
          }

          function scheduleTone(time, frequency, volume, duration, type, endFrequency) {
            if (!audioCtx) return;
            try {
              const osc = audioCtx.createOscillator();
              const gain = audioCtx.createGain();
              osc.type = type || "sine";
              osc.frequency.setValueAtTime(frequency, time);
              if (endFrequency) osc.frequency.exponentialRampToValueAtTime(endFrequency, time + duration);
              gain.gain.setValueAtTime(0.0001, time);
              gain.gain.exponentialRampToValueAtTime(volume, time + 0.006);
              gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
              osc.connect(gain).connect(audioCtx.destination);
              activeOscillators.add(osc);
              osc.onended = function () { activeOscillators.delete(osc); };
              osc.start(time);
              osc.stop(time + duration + 0.02);
            } catch (error) {}
          }

          function scheduleVisual(time, callback) {
            if (!audioCtx) return;
            const delay = Math.max(0, (time - audioCtx.currentTime) * 1000);
            const id = window.setTimeout(function () {
              visualTimers.delete(id);
              callback();
            }, delay);
            visualTimers.add(id);
          }

          function displayCompressionCue(count) {
            if (!count) return;
            if (count === 30) els.beatDisc.dataset.cueActive = "true";
            else delete els.beatDisc.dataset.cueActive;
            setBigNumber(String(count), false);
            els.bigSub.textContent = "compression";
            els.stageMsg.textContent = count === 30 ? "Now give 2 breaths during the brief pause." : MODE.nonintubated.running;
          }

          function animateBreath(inhaleMs, exhaleMs) {
            const meter = els.breathMeter;
            const fill = els.breathFill;
            meter.hidden = false;
            els.breathMeterLabel.textContent = "Breathe in";
            fill.style.transition = "none";
            fill.style.width = "0%";
            void fill.offsetWidth;
            fill.style.transition = "width " + inhaleMs + "ms linear";
            fill.style.width = "100%";
            const outId = window.setTimeout(function () {
              visualTimers.delete(outId);
              els.breathMeterLabel.textContent = "Breathe out";
              fill.style.transition = "width " + exhaleMs + "ms linear";
              fill.style.width = "0%";
            }, inhaleMs);
            visualTimers.add(outId);
            const hideId = window.setTimeout(function () {
              visualTimers.delete(hideId);
              meter.hidden = true;
            }, inhaleMs + exhaleMs + 150);
            visualTimers.add(hideId);
          }

          function displayBreathCue(number, pausesCompressions) {
            els.beatDisc.dataset.cueActive = "true";
            if (number) {
              setBigNumber(String(number), false);
              els.bigSub.textContent = "breathe in · then release";
              els.stageMsg.textContent = "Visible but not excessive chest rise. Resume after breath 2.";
              animateBreath(600, 450);
            } else {
              animateBreath(1000, 1100);
              setBigNumber("IN", true);
              els.bigSub.textContent = "squeeze · about 1 second";
              els.stageMsg.textContent = "Keep compressions going while ventilating.";
              const outId = window.setTimeout(function () {
                visualTimers.delete(outId);
                if (phase !== PHASE.COMPRESSIONS) return;
                setBigNumber("OUT", true);
                els.bigSub.textContent = "let the chest fall";
              }, 1000);
              visualTimers.add(outId);
              const clearId = window.setTimeout(function () {
                visualTimers.delete(clearId);
                delete els.beatDisc.dataset.cueActive;
              }, 2200);
              visualTimers.add(clearId);
            }
            if (pausesCompressions && number === 2) {
              const id = window.setTimeout(function () {
                visualTimers.delete(id);
                delete els.beatDisc.dataset.cueActive;
              }, 1000);
              visualTimers.add(id);
            }
          }

          function popBeat() {
            els.beatDisc.classList.remove("pop");
            void els.beatDisc.offsetWidth;
            els.beatDisc.classList.add("pop");
            const id = window.setTimeout(function () {
              visualTimers.delete(id);
              els.beatDisc.classList.remove("pop");
            }, 70);
            visualTimers.add(id);
          }

          function stopScheduler() {
            if (schedulerId) window.clearInterval(schedulerId);
            if (fallbackId) window.clearTimeout(fallbackId);
            schedulerId = 0;
            fallbackId = 0;
            visualTimers.forEach(function (id) { window.clearTimeout(id); });
            visualTimers.clear();
            activeOscillators.forEach(function (osc) {
              try { osc.stop(); } catch (error) {}
            });
            activeOscillators.clear();
            els.beatDisc.classList.remove("pop");
            delete els.beatDisc.dataset.cueActive;
            els.breathMeter.hidden = true;
            els.breathFill.style.transition = "none";
            els.breathFill.style.width = "0%";
          }

          function testSound() {
            if (!ensureAudio()) {
              showToast("Audio is not supported in this browser. The visual timer still works.");
              return;
            }
            let played = false;
            if (cfg.metro) { scheduleTone(audioCtx.currentTime + 0.04, 1100, 0.32, 0.09, "square"); played = true; }
            if (cfg.breathSound) { scheduleBreathNoise(audioCtx.currentTime + (cfg.metro ? 0.28 : 0.05), 0.7, 0.7); played = true; }
            if (cfg.voice) { say("Sound check.", false); played = true; }
            showToast(played ? "Sound test played. Check your device volume if you did not hear it." : "All three sounds are switched off.");
          }

          function say(text, interrupt) {
            if (!cfg.voice || !("speechSynthesis" in window)) return;
            try {
              if (interrupt) window.speechSynthesis.cancel();
              const utterance = new SpeechSynthesisUtterance(text);
              utterance.rate = 1.03;
              utterance.pitch = 1;
              utterance.volume = 1;
              window.speechSynthesis.speak(utterance);
            } catch (error) {}
          }

          function cancelSpeech() {
            try { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); } catch (error) {}
          }

          async function requestWakeLock() {
            if (!cfg.wake || !running || !("wakeLock" in navigator) || document.visibilityState !== "visible") return;
            const token = ++wakeRequestToken;
            try {
              const requested = await navigator.wakeLock.request("screen");
              if (token !== wakeRequestToken || !cfg.wake || !running) {
                await requested.release();
                return;
              }
              if (wakeLock && wakeLock !== requested) await wakeLock.release();
              wakeLock = requested;
              wakeLock.addEventListener("release", function () {
                if (wakeLock === requested) wakeLock = null;
                updateSupportStatus();
              });
              updateSupportStatus();
            } catch (error) {
              updateSupportStatus("Screen wake lock was unavailable; keep the device awake manually.");
            }
          }

          async function releaseWakeLock() {
            wakeRequestToken += 1;
            const current = wakeLock;
            wakeLock = null;
            if (!current) return;
            try { await current.release(); } catch (error) {}
            updateSupportStatus();
          }

          function updateSupportStatus(message) {
            if (message) {
              els.supportStatus.textContent = message;
              return;
            }
            if (cfg.wake && wakeLock) els.supportStatus.textContent = "Screen awake lock active.";
            else if (cfg.wake && !("wakeLock" in navigator)) els.supportStatus.textContent = "Wake lock unavailable; keep the screen awake manually.";
            else els.supportStatus.textContent = "Timer works without an internet connection once this page is loaded.";
          }

          function openEndDialog() {
            if (!isEventActive()) return;
            if (typeof els.endDialog.showModal === "function") els.endDialog.showModal();
            else if (window.confirm("Finish this CPR event as ROSC achieved?")) finishEvent("ROSC achieved");
          }

          function closeEndDialog() {
            if (els.endDialog.open) els.endDialog.close();
          }

          async function toggleFullscreen() {
            try {
              if (!document.fullscreenElement) await els.root.requestFullscreen();
              else await document.exitFullscreen();
            } catch (error) {
              showToast("Full screen is not available in this browser.");
            }
          }

          function updateFullscreenButton() {
            els.fullscreenBtn.textContent = document.fullscreenElement ? "Exit full screen" : "Full screen";
          }

          function showToast(message) {
            window.clearTimeout(toastTimer);
            els.toast.textContent = message;
            els.toast.hidden = false;
            toastTimer = window.setTimeout(function () { els.toast.hidden = true; }, 3500);
          }

    }
  }

  customElements.define(ELEMENT_NAME, PetCprTimer);
})();
